import React from 'react';
import {Box, Flex, Grid, Skeleton, Spinner, Text} from '@chakra-ui/react';
import {TablerChevronDown, TablerChevronUp} from '../../icons';
import {isNonEnglish} from '../../constants/designTokens';
import useProgressiveCount from './useProgressiveCount';
import {clean as cleanText} from './plannerModels';
import {
  formatPlannerSidebarTime,
  localDateKey,
  plannerDateGroupLabel,
  toPlannerDate,
} from './plannerDateTime';
import PatientBrowserCard, {
  PLANNER_SIDEBAR_DATE_COLUMN_WIDTH,
} from './PatientBrowserCard';
import type {
  PatientEncounterRow,
  PatientIdentityRow,
} from './PatientBrowserCard';

export {formatPlannerSidebarTime, plannerDateGroupLabel};
export {
  PLANNER_SIDEBAR_DATE_COLUMN_WIDTH,
  PLANNER_SIDEBAR_DATE_CONTENT_OFFSET,
  PLANNER_SIDEBAR_NAME_FONT_SIZE,
  plannerLanguageMarker,
} from './PatientBrowserCard';

export interface PlannerBrowserRecordRow {
  animateOnEnter?: boolean;
  key?: React.Key | null;
  ariaLabel?: string;
  cardKind?: string;
  contextNode?: React.ReactNode;
  contextText?: string;
  detailLines?: Array<string | Record<string, any>>;
  encounterRows?: PatientEncounterRow[];
  identityRow?: PatientIdentityRow | false | null;
  identityRowTestId?: string;
  isSelected?: boolean;
  language?: string;
  name?: string;
  nameFontWeight?: string;
  nameNoOfLines?: number;
  nameNode?: React.ReactNode;
  nameWhiteSpace?: string;
  onSelect?: () => void;
  rowEndSlot?: React.ReactNode;
  rowProps?: Record<string, any>;
  showBottomDivider?: boolean;
  showIdentityRow?: boolean;
  singleLine?: boolean;
  timestamp?: any;
  time?: string;
  timeDetail?: string;
  wrapName?: boolean;
}

const EMPTY_ENTERING_KEYS: Set<any> = new Set();

// Gentle fade + lift for rows that arrive after the list has already painted
// (e.g. lazily-loaded company phone calls or appended pagination pages). Rows
// present on first paint don't get this — they ride in with the page fade.
const ROW_ENTER_SX = {
  animation: 'plannerRowEnter 280ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
  '@keyframes plannerRowEnter': {
    '0%': {opacity: 0, transform: 'translateY(6px)'},
    '100%': {opacity: 1, transform: 'translateY(0)'},
  },
};

const ROW_ENTER_CLEAR_MS = 360;

// Render-skip for rows far outside the viewport: planner lists mount every
// fetched row (no windowing — variable heights make scroll estimation janky),
// so a deep Recent scroll accumulates DOM. content-visibility lets the browser
// skip layout/paint for off-screen rows, keeping scroll cost flat regardless
// of depth, while rows stay in the DOM and accessibility tree. Applied to the
// card INTERIOR, not the row surface: containment clips descendant paint to
// the box, which would eat the divider ::after (drawn 1px past the row) and
// selection chrome if applied at the surface. The intrinsic-size fallback
// seeds unrendered rows with a near-real height for scrollbar geometry; once
// a row has rendered, `auto` remembers its exact size.
const CARD_CONTENT_VISIBILITY_SX = {
  contentVisibility: 'auto',
  containIntrinsicSize: 'auto 56px',
};
export const PLANNER_POINTER_HOVER_TRANSITION =
  'background-color 60ms ease-out, border-color 60ms ease-out, border-radius 60ms ease-out';
export const PLANNER_POINTER_HOVER_TRANSITION_WITH_COLOR = `${PLANNER_POINTER_HOVER_TRANSITION}, color 60ms ease-out`;

// Tracks which row keys are newly present since the last render so only late
// arrivals animate. The first commit (and every render while disabled) primes
// the seen set without animating, so toggling animation on later doesn't flash
// the whole list at once.
function useEnteringRowKeys(keys: any[], enabled: boolean) {
  const seenRef = React.useRef<Set<any>>(new Set());
  const initializedRef = React.useRef(false);
  const [entering, setEntering] = React.useState<Set<any>>(EMPTY_ENTERING_KEYS);
  const signature = keys.join('\u0000');

  React.useEffect(() => {
    if (!enabled) {
      seenRef.current = new Set(keys);
      initializedRef.current = true;
      if (entering.size) setEntering(EMPTY_ENTERING_KEYS);
      return undefined;
    }

    const seen = seenRef.current;
    if (!initializedRef.current) {
      initializedRef.current = true;
      keys.forEach(key => key != null && seen.add(key));
      return undefined;
    }

    const fresh: any[] = [];
    keys.forEach(key => {
      if (key == null || seen.has(key)) return;
      seen.add(key);
      fresh.push(key);
    });

    if (!fresh.length) return undefined;
    setEntering(new Set(fresh));
    const timer = setTimeout(
      () => setEntering(EMPTY_ENTERING_KEYS),
      ROW_ENTER_CLEAR_MS,
    );
    return () => clearTimeout(timer);
    // `signature` captures the visible key set; `keys` is read fresh inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled]);

  return enabled ? entering : EMPTY_ENTERING_KEYS;
}

// Ghost rows shown while a list's first page is in flight, in place of the
// old centered spinner / premature empty text. Mirrors the real row geometry
// (time column + name and meta lines, row dividers) so loaded content replaces
// it without a layout jump. The block fades in so a fast response never
// flashes a skeleton frame.
const SKELETON_FADE_SX = {
  animation: 'plannerSkeletonFadeIn 240ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
  '@keyframes plannerSkeletonFadeIn': {
    '0%': {opacity: 0},
    '100%': {opacity: 1},
  },
};

export const PlannerListSkeleton = ({
  rows = 6,
  showTimeColumn = true,
  showGroupHeader = true,
  styles = {},
  testId = 'planner-list-skeleton',
  ariaLabel = 'Loading',
}: Record<string, any>) => (
  <Box
    data-testid={testId}
    role="status"
    aria-label={ariaLabel}
    aria-busy="true"
    sx={SKELETON_FADE_SX}
  >
    {/* Below lg the column stacks above the greeting + New Encounter form,
        so loading shows only a slim spinner there — ghost rows of any count
        read as a broken pile of bars and shoved the page below the fold. */}
    <Flex
      data-testid={`${testId}-compact`}
      display={{base: 'flex', lg: 'none'}}
      align="center"
      justify="center"
      py={10}
    >
      <Spinner
        boxSize="28px"
        color="opalite.600"
        thickness="2.5px"
        speed="0.7s"
      />
    </Flex>
    <Box display={{base: 'none', lg: 'block'}}>
      {showGroupHeader && (
        <Skeleton
          h="0.75rem"
          w="5.5rem"
          borderRadius="md"
          mt={2}
          mb={4}
          ml={3}
        />
      )}
      {Array.from({length: rows}).map((_, index) => (
        <Grid
          key={index}
          // Same column template as the real rows (PatientBrowserCard), so
          // the time-column rhythm holds steady when content replaces the
          // skeleton — a plain Flex here read as a misaligned stack.
          templateColumns={
            showTimeColumn
              ? {
                  base: `${PLANNER_SIDEBAR_DATE_COLUMN_WIDTH.base} minmax(0, 1fr)`,
                  md: `${PLANNER_SIDEBAR_DATE_COLUMN_WIDTH.md} minmax(0, 1fr)`,
                }
              : 'minmax(0, 1fr)'
          }
          columnGap={2}
          alignItems="start"
          px={3}
          py={3}
          minH="64px"
          borderBottomWidth={index < rows - 1 ? '1px' : 0}
          borderBottomStyle="solid"
          borderBottomColor={
            styles?.listRowDividerColor || styles?.rowBorderColor || 'gray.100'
          }
        >
          {showTimeColumn && (
            <Skeleton h="0.8rem" w="3.25rem" mt="1px" borderRadius="md" />
          )}
          <Box minW={0}>
            <Skeleton
              h="0.85rem"
              w={index % 2 ? '46%' : '62%'}
              maxW="16rem"
              borderRadius="md"
            />
            <Skeleton
              h="0.65rem"
              w={index % 2 ? '58%' : '40%'}
              maxW="13rem"
              borderRadius="md"
              mt={2}
            />
          </Box>
        </Grid>
      ))}
    </Box>
  </Box>
);

export function plannerNavigationContextText({
  language = '',
  context = '',
} = {}) {
  const languageText = isNonEnglish(language) ? cleanText(language) : '';
  const contextText = cleanText(context);
  if (!languageText) return contextText;
  if (!contextText) return languageText;
  if (contextText.toLowerCase().includes(languageText.toLowerCase()))
    return contextText;
  return `${languageText} • ${contextText}`;
}

export function plannerNavigationAriaLabel({
  time = '',
  name = '',
  language = '',
  dialect = '',
  contextText = '',
} = {}) {
  const languageText = isNonEnglish(language) ? cleanText(language) : '';
  const context = cleanText(contextText);
  const contextIncludesLanguage =
    languageText && context.toLowerCase().includes(languageText.toLowerCase());

  return [
    'View',
    name || 'Unknown patient',
    time ? `at ${time}` : '',
    contextIncludesLanguage ? '' : languageText,
    context,
  ]
    .filter(Boolean)
    .join(' ');
}

const normalizeSortDirection = (value: any) =>
  value === 'asc' ? 'asc' : 'desc';

function compareSortEntries(left: any, right: any, sortDirection = 'desc') {
  const leftValue = left.sortValue;
  const rightValue = right.sortValue;

  if (leftValue == null && rightValue == null) return left.index - right.index;
  if (leftValue == null) return 1;
  if (rightValue == null) return -1;
  if (leftValue !== rightValue) {
    return sortDirection === 'asc'
      ? leftValue - rightValue
      : rightValue - leftValue;
  }
  return left.index - right.index;
}

function nextGroupSortValue(
  currentValue: any,
  rowValue: any,
  sortDirection: any,
) {
  if (currentValue == null) return rowValue;
  if (rowValue == null) return currentValue;
  return sortDirection === 'asc'
    ? Math.min(currentValue, rowValue)
    : Math.max(currentValue, rowValue);
}

export const PlannerNavigationSectionHeader = ({
  asButton = true,
  bg,
  contentId,
  count,
  isCollapsed = false,
  isCollapsible = false,
  label,
  labelTestId = 'planner-section-header-label',
  onToggle,
  styles,
  testId = 'planner-section-header',
}: Record<string, any>) => {
  const ChevronIcon = isCollapsed ? TablerChevronDown : TablerChevronUp;
  const headerBg = bg || styles?.rowBg || 'transparent';
  const buttonProps: Record<string, any> = asButton
    ? {
        as: 'button',
        type: 'button',
        cursor: isCollapsible ? 'pointer' : 'default',
        'aria-controls': isCollapsible ? contentId : undefined,
        'aria-expanded': isCollapsible ? !isCollapsed : undefined,
        onClick: isCollapsible ? onToggle : undefined,
      }
    : {
        as: 'div',
        cursor: 'default',
      };

  return (
    <Box
      {...buttonProps}
      data-testid={testId}
      data-date-group-bg={headerBg}
      px={0}
      py={1.5}
      mb={0.5}
      position="sticky"
      top={0}
      zIndex={1}
      bg={headerBg}
      border={0}
      textAlign="left"
      w="100%"
      _hover={{bg: headerBg}}
      _focusVisible={styles.focusOutlineStyles}
    >
      <Flex align="center" justify="space-between" gap={3} w="100%">
        <Text
          as="h3"
          data-testid={labelTestId}
          data-font-weight="semibold"
          color={styles.sectionLabelColor}
          fontSize="md"
          fontWeight="semibold"
          lineHeight="1.25"
        >
          {label}
        </Text>
        <Flex align="center" gap={2} flexShrink={0}>
          <Box
            bg={styles.countBg}
            color={styles.countColor}
            borderRadius="md"
            px={2.5}
            py={0.5}
            minW="2.25rem"
            textAlign="center"
            fontSize="md"
            fontWeight="normal"
            lineHeight="1.4"
          >
            {count}
          </Box>
          {isCollapsible && (
            <Box
              as="span"
              aria-hidden="true"
              color={styles.sectionLabelColor}
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <ChevronIcon size={16} focusable="false" />
            </Box>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export function groupPlannerRowsByDate(
  items: any[] = [],
  getTimestamp: (item?: any) => any = () => null,
  now: Date = new Date(),
  {dateGrouping = 'past', sortDirection = 'desc'}: Record<string, any> = {},
) {
  const normalizedSortDirection = normalizeSortDirection(sortDirection);
  const groupsByKey = new Map();

  items.forEach((item, index) => {
    const timestamp = toPlannerDate(getTimestamp(item));
    const key = timestamp ? localDateKey(timestamp) : 'no-date';
    const existing = groupsByKey.get(key);
    const row = {
      item,
      index,
      timestamp,
      sortValue: timestamp?.getTime?.() ?? null,
    };

    if (existing) {
      existing.rows.push(row);
      existing.sortValue = nextGroupSortValue(
        existing.sortValue,
        row.sortValue,
        normalizedSortDirection,
      );
      return;
    }

    groupsByKey.set(key, {
      key,
      label: plannerDateGroupLabel(timestamp, now, {direction: dateGrouping}),
      index,
      sortValue: row.sortValue,
      rows: [row],
    });
  });

  return [...groupsByKey.values()]
    .sort((left, right) =>
      compareSortEntries(left, right, normalizedSortDirection),
    )
    .map(group => ({
      ...group,
      rows: [...group.rows].sort((left, right) =>
        compareSortEntries(left, right, normalizedSortDirection),
      ),
    }));
}

export const PLANNER_OPALITE_SELECTION_RADIUS = 'lg';

export const patientBrowserCardSurfaceColors = (
  styles: Record<string, any> = {},
) => {
  const selectedBg = styles?.listRowSelectedBg || 'gray.200';
  const selectedBorderColor = styles?.listRowSelectedBorderColor || selectedBg;
  const hoverBg = selectedBg;

  return {
    selectedBg,
    selectedBorderColor,
    hoverBg,
    selectionRadius: PLANNER_OPALITE_SELECTION_RADIUS,
    dividerColor:
      styles?.listRowDividerColor || styles?.rowBorderColor || 'gray.200',
  };
};

export const PlannerBrowserCardWrapper = ({
  cardKind = 'base',
  ...props
}: Record<string, any>) => (
  <PatientBrowserCard {...props} cardKind={cardKind} />
);

export const PlannerListItem = React.memo(
  ({
    cardKind = 'base',
    children = null,
    encounterRows = [],
    time = '',
    name = '',
    language = '',
    contextText = '',
    contextNode = null,
    detailLines = [],
    identityRow = null,
    identityRowTestId = null,
    showIdentityRow = true,
    isSelected = false,
    singleLine = false,
    wrapName = false,
    nameFontWeight = null,
    nameNoOfLines = null,
    nameWhiteSpace = null,
    nameNode = null,
    rowEndSlot = null,
    showBottomDivider = true,
    surfaceColors = null,
    onSelect,
    ariaLabel,
    showTimeColumn = true,
    timeDetail = '',
    interactionsDisabled = false,
    styles,
    ...surfaceProps
  }: Record<string, any>) => {
    const canSelect = typeof onSelect === 'function';
    const colors = surfaceColors || patientBrowserCardSurfaceColors(styles);
    const selectedColor =
      styles?.listRowSelectedColor || styles?.rowActionColor;
    const selectedTimeColor = styles?.listRowSelectedTimeColor || selectedColor;
    const isDropdownOption = surfaceProps.role === 'option';
    const label =
      ariaLabel ||
      plannerNavigationAriaLabel({
        time,
        name,
        language,
        contextText,
      });
    const hasEndSlot = Boolean(rowEndSlot);
    const cardStyles = {
      ...styles,
      rowActionColor: isSelected ? selectedColor : styles?.rowActionColor,
      rowTimeColor: isSelected ? selectedTimeColor : styles?.rowTimeColor,
    };
    const {
      onClick: surfaceOnClick,
      sx: surfaceSx,
      ...restSurfaceProps
    } = surfaceProps;
    const resolvedIdentityRow =
      identityRow ||
      (showIdentityRow && name
        ? {
            kind: 'label',
            label: name,
            language,
            metadataLines: contextText
              ? [{text: contextText, node: contextNode}]
              : [],
            nameNode,
          }
        : null);

    const handleSelect = (event?: any) => {
      if (interactionsDisabled) {
        event?.preventDefault?.();
        return;
      }
      surfaceOnClick?.(event);
      if (!event?.defaultPrevented) onSelect?.();
    };

    const dropdownOptionFocusStyles = {
      bg: isSelected ? colors.selectedBg : colors.hoverBg,
      borderRadius: colors.selectionRadius,
      outline: 'none',
      boxShadow: 'none',
    };
    const interactiveSurfaceStyles =
      canSelect && !interactionsDisabled
        ? {
            _hover: {
              bg: isSelected ? colors.selectedBg : colors.hoverBg,
              borderRadius: colors.selectionRadius,
            },
            _active: {bg: colors.selectedBg},
          }
        : {};
    const dividerStyles = showBottomDivider
      ? {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: '-1px',
          borderBottom: '1px solid',
          borderColor: colors.dividerColor,
          pointerEvents: 'none',
          transition: 'border-color 60ms ease-out',
        }
      : undefined;
    const dividerHoverSx: Record<string, any> | undefined =
      showBottomDivider && canSelect && !interactionsDisabled
        ? {
            '&:hover::after, &:has(+ [data-planner-row-surface="true"]:hover)::after':
              {
                borderColor: 'transparent',
              },
          }
        : undefined;
    const mergedSurfaceSx =
      dividerHoverSx || surfaceSx
        ? {...(dividerHoverSx || {}), ...(surfaceSx || {})}
        : undefined;
    const card = (
      <PlannerBrowserCardWrapper
        cardKind={cardKind}
        contextNode={contextNode}
        contextText={contextText}
        detailLines={detailLines}
        encounterRows={encounterRows}
        identityRow={resolvedIdentityRow as any}
        identityRowTestId={identityRowTestId}
        language={language}
        name={name}
        nameFontWeight={nameFontWeight}
        nameNoOfLines={nameNoOfLines}
        nameNode={nameNode}
        nameWhiteSpace={nameWhiteSpace}
        showIdentityRow={showIdentityRow}
        showTimeColumn={showTimeColumn}
        singleLine={singleLine}
        styles={cardStyles}
        time={time}
        timeDetail={timeDetail}
        wrapName={wrapName}
      >
        {!hasEndSlot ? children : null}
      </PlannerBrowserCardWrapper>
    );
    // Dropdown options skip the render-skip wrapper: the popper measures the
    // open menu, and estimated (not yet rendered) option heights would let it
    // mis-place before correcting. List rows get it — see the const's comment.
    const cardNode = isDropdownOption ? (
      card
    ) : (
      <Box minW={0} w="100%" sx={CARD_CONTENT_VISIBILITY_SX}>
        {card}
      </Box>
    );

    if (hasEndSlot) {
      return (
        <Grid
          data-testid="planner-row-surface"
          data-planner-row-surface="true"
          data-patient-browser-card-surface={cardKind}
          data-interactions-disabled={interactionsDisabled ? 'true' : 'false'}
          data-selected={isSelected ? 'true' : 'false'}
          data-show-bottom-divider={showBottomDivider ? 'true' : 'false'}
          templateColumns="minmax(0, 1fr) auto"
          alignItems="stretch"
          minW={0}
          pointerEvents={interactionsDisabled ? 'none' : 'auto'}
          position="relative"
          borderRadius={isSelected ? colors.selectionRadius : '0'}
          borderWidth="1px"
          borderColor={isSelected ? colors.selectedBorderColor : 'transparent'}
          borderBottomColor={
            isSelected ? colors.selectedBorderColor : 'transparent'
          }
          bg={isSelected ? colors.selectedBg : 'transparent'}
          transition={PLANNER_POINTER_HOVER_TRANSITION}
          sx={dividerHoverSx}
          _after={dividerStyles}
          {...interactiveSurfaceStyles}
          _focusWithin={styles?.focusOutlineStyles}
        >
          <Box
            as={canSelect ? 'button' : 'div'}
            {...({type: canSelect ? 'button' : undefined} as any)}
            appearance="none"
            bg="transparent"
            border={0}
            color={isSelected ? selectedColor : styles?.rowActionColor}
            cursor={canSelect ? 'pointer' : 'default'}
            fontFamily="inherit"
            minW={0}
            textAlign="left"
            w="100%"
            {...restSurfaceProps}
            aria-disabled={
              canSelect && interactionsDisabled ? 'true' : undefined
            }
            aria-current={canSelect && isSelected ? 'true' : undefined}
            aria-label={canSelect ? label : undefined}
            data-patient-browser-card-surface={cardKind}
            data-selected={isSelected ? 'true' : 'false'}
            data-show-bottom-divider="false"
            data-show-time-column={showTimeColumn ? 'true' : 'false'}
            disabled={canSelect && interactionsDisabled ? true : undefined}
            onClick={canSelect ? handleSelect : surfaceOnClick}
            sx={surfaceSx}
          >
            {cardNode}
          </Box>
          <Flex
            data-testid="planner-row-end-slot"
            align="flex-start"
            justify="center"
            py={2}
            pr={2}
            cursor={canSelect ? 'pointer' : 'default'}
            onClick={interactionsDisabled ? undefined : handleSelect}
          >
            {rowEndSlot}
          </Flex>
          {children && (
            <Box gridColumn="1 / -1" minW={0}>
              {children}
            </Box>
          )}
        </Grid>
      );
    }

    return (
      <Box
        as={canSelect ? 'button' : 'div'}
        {...({type: canSelect ? 'button' : undefined} as any)}
        appearance="none"
        bg={isSelected ? colors.selectedBg : 'transparent'}
        borderWidth="1px"
        borderColor={isSelected ? colors.selectedBorderColor : 'transparent'}
        borderBottomColor={
          isSelected ? colors.selectedBorderColor : 'transparent'
        }
        borderRadius={isSelected ? colors.selectionRadius : '0'}
        color={isSelected ? selectedColor : styles?.rowActionColor}
        cursor={canSelect ? 'pointer' : 'default'}
        data-patient-browser-card-surface={cardKind}
        data-planner-row-surface="true"
        data-interactions-disabled={interactionsDisabled ? 'true' : 'false'}
        data-selected={isSelected ? 'true' : 'false'}
        data-show-bottom-divider={showBottomDivider ? 'true' : 'false'}
        data-show-time-column={showTimeColumn ? 'true' : 'false'}
        fontFamily="inherit"
        minW={0}
        pointerEvents={interactionsDisabled ? 'none' : 'auto'}
        position="relative"
        textAlign="left"
        transition={PLANNER_POINTER_HOVER_TRANSITION_WITH_COLOR}
        w="100%"
        sx={mergedSurfaceSx}
        _after={dividerStyles}
        {...interactiveSurfaceStyles}
        _focus={
          isDropdownOption && canSelect ? dropdownOptionFocusStyles : undefined
        }
        _focusVisible={
          isDropdownOption && canSelect
            ? dropdownOptionFocusStyles
            : styles?.focusOutlineStyles
        }
        {...restSurfaceProps}
        aria-disabled={canSelect && interactionsDisabled ? 'true' : undefined}
        aria-current={canSelect && isSelected ? 'true' : undefined}
        aria-label={canSelect ? label : undefined}
        disabled={canSelect && interactionsDisabled ? true : undefined}
        onClick={canSelect ? handleSelect : surfaceOnClick}
      >
        {cardNode}
      </Box>
    );
  },
);

export default PlannerListItem;

export const PlannerList = ({
  rows = [],
  emptyText = '',
  styles,
  dateGroupBg = null,
  groupRowsInCard = false,
  groupRowsCardBg = null,
  progressiveKey = 'date-grouped',
  dateGrouping = 'past',
  sortDirection = 'desc',
  showTimeColumn = true,
  initialVisibleCount = null,
  preserveVisibleCountOnChange = false,
  onVisibleCountChange,
  progressive = true,
  collapsibleDateGroups = true,
  animateEntries = false,
  interactionsDisabled = false,
  testId = undefined,
}: Record<string, any>) => {
  const surfaceColors = React.useMemo(
    () => patientBrowserCardSurfaceColors(styles),
    [styles],
  );
  const [collapsedDateGroups, setCollapsedDateGroups] = React.useState(
    () => new Set(),
  );
  // Progressive mount spreads a large list's first render across animation
  // frames (see useProgressiveCount) so it never blocks the main thread. It
  // only grows the count from the top and never windows, so it needs no
  // row-height estimate and can't shift layout mid-scroll. Rows always lay out
  // at their natural height — the source of truth, not a guessed constant.
  const progressiveVisibleCount = useProgressiveCount(
    rows.length,
    `${progressiveKey}:${rows.length}:${rows[0]?.key || ''}:${rows[rows.length - 1]?.key || ''}`,
    32,
    32,
    {
      initialVisibleCount,
      preserveOnReset: preserveVisibleCountOnChange,
      onCountChange: onVisibleCountChange,
      disabled: !progressive,
    },
  );
  const visibleCount = progressive ? progressiveVisibleCount : rows.length;
  const visibleRows = React.useMemo(
    () => rows.slice(0, visibleCount),
    [rows, visibleCount],
  );
  const enteringRowKeys = useEnteringRowKeys(
    visibleRows.map((row: any) => row?.key),
    animateEntries,
  );
  const groups =
    dateGrouping === 'none'
      ? []
      : groupPlannerRowsByDate(
          visibleRows,
          (row: any) => row?.timestamp,
          new Date(),
          {
            dateGrouping,
            sortDirection,
          },
        );
  const handleDateGroupToggle = React.useCallback(
    (groupKey: any) => {
      if (!collapsibleDateGroups) return;
      setCollapsedDateGroups(previous => {
        const next = new Set(previous);
        if (next.has(groupKey)) next.delete(groupKey);
        else next.add(groupKey);
        return next;
      });
    },
    [collapsibleDateGroups],
  );

  if (!rows.length) {
    return (
      <Text
        color={styles.metaTextColor}
        fontSize="md"
        fontWeight="normal"
        lineHeight="1.4"
        px={3}
        py={3}
        textAlign="left"
      >
        {emptyText}
      </Text>
    );
  }

  const renderRows = (
    rows: any,
    {testId: rowsTestId = undefined}: Record<string, any> = {},
  ) => (
    <Box display="grid" gap={0} data-testid={rowsTestId}>
      {rows.map((row: any, index: any) => {
        const {
          animateOnEnter = true,
          cardKind = 'base',
          key,
          time = '',
          name = '',
          language = '',
          contextText = '',
          ariaLabel,
          isSelected = false,
          onSelect,
          rowEndSlot = null,
          showBottomDivider: rowShowBottomDivider,
          rowProps = {},
          timestamp: _timestamp,
          ...cardProps
        } = row || {};
        const {
          patientBrowserCardKind,
          rowEndSlot: rowPropsEndSlot = null,
          rowFadeBg: _rowFadeBg = null,
          showBottomDivider: rowPropsShowBottomDivider,
          sx: rowPropsSx,
          ...rowRest
        } = rowProps || {};
        const {sx: cardSx, ...restCardProps} = cardProps;
        const rowKey = key || index;
        const nextRow = rows[index + 1] || {};
        const isEntering =
          animateEntries &&
          animateOnEnter !== false &&
          key != null &&
          enteringRowKeys.has(key);
        const shouldAnimateRow = isEntering;
        const handleRowSelect =
          typeof onSelect === 'function' ? onSelect : undefined;
        const label =
          ariaLabel ||
          plannerNavigationAriaLabel({
            time,
            name,
            language,
            contextText,
          });
        const resolvedCardKind =
          cardKind === 'base' && patientBrowserCardKind
            ? patientBrowserCardKind
            : cardKind;
        const rowIsSelected = Boolean(isSelected);
        const nextRowIsSelected = Boolean(nextRow?.isSelected);
        const showBottomDivider =
          !rowIsSelected && !nextRowIsSelected && index < rows.length - 1;
        const endSlot = rowEndSlot || rowPropsEndSlot;
        const resolvedShowBottomDivider =
          rowPropsShowBottomDivider ??
          rowShowBottomDivider ??
          showBottomDivider;
        const rowContent = (
          <PlannerListItem
            key={rowKey}
            {...restCardProps}
            cardKind={resolvedCardKind}
            time={time}
            name={name}
            language={language}
            contextText={contextText}
            isSelected={rowIsSelected}
            interactionsDisabled={interactionsDisabled}
            styles={styles}
            surfaceColors={surfaceColors}
            ariaLabel={handleRowSelect ? label : undefined}
            showTimeColumn={showTimeColumn}
            showBottomDivider={!endSlot && resolvedShowBottomDivider}
            onSelect={handleRowSelect}
            {...rowRest}
            data-row-entering={shouldAnimateRow ? 'true' : 'false'}
            rowEndSlot={endSlot}
            sx={shouldAnimateRow ? ROW_ENTER_SX : rowPropsSx || cardSx}
          />
        );
        return rowContent;
      })}
    </Box>
  );

  if (dateGrouping === 'none') {
    return renderRows(visibleRows, {testId});
  }

  const groupBg = dateGroupBg || styles?.rowBg || 'transparent';
  const rowCardBg = groupRowsCardBg || styles?.rowBg || 'white';

  const renderGroupRows = (rows: any) => {
    const groupedRows = rows.map((row: any) => row.item);
    if (!groupRowsInCard) return renderRows(groupedRows);
    return (
      <Box
        data-testid="planner-date-group-card"
        data-group-card-bg={rowCardBg}
        bg={rowCardBg}
        borderWidth="1px"
        borderColor={styles?.rowBorderColor || 'gray.200'}
        borderRadius="lg"
        overflow="hidden"
      >
        {renderRows(groupedRows)}
      </Box>
    );
  };

  return (
    <Box display="grid" gap={2} bg={groupBg}>
      {groups.map(group => {
        const isCollapsed =
          collapsibleDateGroups && collapsedDateGroups.has(group.key);
        const contentId = `${progressiveKey}-${group.key}-date-group`;

        return (
          <Box key={group.key}>
            <PlannerNavigationSectionHeader
              bg={groupBg}
              contentId={contentId}
              count={group.rows.length}
              isCollapsed={isCollapsed}
              isCollapsible={collapsibleDateGroups}
              label={group.label}
              onToggle={() => handleDateGroupToggle(group.key)}
              styles={styles}
              labelTestId="planner-date-group-label"
              testId="planner-date-group-header"
            />

            {!isCollapsed && (
              <Box id={contentId} px={0} pt={0} pb={0} bg={groupBg}>
                {renderGroupRows(group.rows)}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
